<!--
    The default rendering pipes `:icon="..."` through `<e-icon>`, which
    expects a string icon name (Unicons / pi-*). Callers that want to
    render a non-string icon — e.g. a Phosphor SFC where the icon is a
    Vue component handed in via `iconComponent`, see
    `CorporateStudentSidebar` — can override the entire icon glyph by
    providing the `#icon` slot. The slot fallback preserves the existing
    `<e-icon>` rendering for every consumer that does NOT provide it, so
    this is purely additive (no migration required for sidebars that
    keep using string icons, e.g. `CorporateAdminSidebar`). The four
    occurrences mirror the link/no-link × tooltip/no-tooltip matrix
    above; keep them in sync.
-->
<template>
    <li class="is-sidebar-summary-item">
        <div class="sidebar-badge-container" :class="{ 'is-summary-active': active }">
            <e-tooltip v-if="label" :label="label" position="is-right">
                <router-link v-if="to" :id="id" :to="to" class="tw:text-[var(--text-color)]!" :aria-label="label">
                    <slot name="icon">
                        <e-icon class="tw:relative tw:top-1" :icon="icon" />
                    </slot>
                </router-link>
                <div v-else class="tw:text-[var(--text-color)]!" @click="$emit('open')">
                    <slot name="icon">
                        <e-icon class="tw:relative tw:top-1" :icon="icon" />
                    </slot>
                </div>
            </e-tooltip>
            <div v-else>
                <router-link v-if="to" :id="id" :to="to" class="tw:text-[var(--text-color)]!">
                    <slot name="icon">
                        <e-icon class="tw:relative tw:top-1" :icon="icon" />
                    </slot>
                </router-link>
                <div v-else class="tw:text-[var(--text-color)]!" @click="$emit('open')">
                    <slot name="icon">
                        <e-icon class="tw:relative tw:top-1" :icon="icon" />
                    </slot>
                </div>
            </div>
            <div v-if="hasBadge" class="sidebar-badge">
                {{ badgeCount }}
            </div>
        </div>

        <small v-if="title" class="is-sidebar-summary-item-title">
            {{ title }}
        </small>
    </li>
</template>

<script>
export default {
    name: 'AppSidebarSummaryItem',

    props: {
        label: {
            default: null,
            type: String,
        },
        id: {
            type: String,
            required: false,
            default: '',
        },
        type: {
            default: null,
            type: String,
        },
        icon: {
            default: null,
            type: String,
        },
        title: {
            default: null,
            type: String,
        },
        to: {
            default: null,
            required: false,
            validator: prop => typeof prop === 'object' || prop === null,
        },
        hasBadge: {
            default: false,
            type: Boolean,
        },
        badgeCount: {
            default: 0,
            type: Number,
        },
        active: {
            default: false,
            type: Boolean,
        },
    },

    emits: ['open'],
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

.sidebar-badge-container {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: visible !important;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    transition: background-color 0.2s ease;
}

.sidebar-badge-container:has(.router-link-exact-active),
.sidebar-badge-container.is-summary-active {
    background-color: var(--p-primary-color);
    opacity: 0.8;

    .e-icon,
    svg,
    i {
        color: white !important;
        fill: white !important;
    }

    // Slotted Phosphor icons (provided by sidebars like CorporateStudentSidebar
    // / CorporateAdminSidebar through SidebarMenu's `#summaryIcon` slot) are
    // rendered with the *calling* sidebar's scope id, not this component's,
    // so the un-prefixed `svg` selector above can't reach them inside
    // <style scoped>. `:deep(svg)` lifts the scope guard. Phosphor paths use
    // `fill="currentColor"`, so `color: white` on the <svg> is what actually
    // recolors the glyph; `fill` is kept as a defensive override for any
    // future SVG that hardcodes a non-currentColor fill.
    :deep(svg) {
        color: white !important;
        fill: white !important;
    }
}

.sidebar-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    border-radius: 12px;
    background: var(--primary-color);
    color: white;
    font-weight: bold;
    height: 18px;
    font-size: 10px;
    text-align: center;
    line-height: 18px;
    min-width: 18px;
    padding: 0 3px;
    z-index: 1;
}

.is-sidebar-summary-item {
    // Also handle non-link items with click handlers
    .sidebar-badge-container:not(:has(.router-link-active)):not(.is-summary-active):hover {
        background-color: var(--p-primary-50);

        opacity: 0.8;

        .e-icon,
        svg,
        i {
            color: var(--text-color);
            fill: var(--text-color);
        }
    }

    .button {
        &.is-primary,
        &.is-primary-light,
        &.is-primary-dark {
            background-color: var(--primary-color) !important;
            border-color: var(--primary-color) !important;
            color: white !important;

            &:hover,
            &:focus,
            &:active,
            &.router-link-active {
                background-color: var(--primary-color) !important;
                border-color: var(--primary-color) !important;
                opacity: 0.9;
            }
        }
    }

    .button.is-primary,
    .button.is-primary-light,
    .button.is-primary-dark {
        .e-icon,
        svg,
        i {
            color: white !important;
            fill: white !important;
        }
    }
}
</style>
